/**
 * IndianFamilyTreeFestivalDetail — one festival's full profile.
 *
 * URL: /indian-family-tree/festivals/:slug
 *
 * Long-form page with: name, also-known-as list, tradition, regions,
 * typical date, and the full family customs paragraph — same aged
 * parchment aesthetic as the calendar index.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Calendar, Flame, Loader2, MapPin, ScrollText,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const P = {
  bg:       "#F5EAD3",
  bgDeep:   "#E8D9B5",
  ink:      "#3A1F0F",
  inkSoft:  "#6B4A2E",
  saffron:  "#B25D38",
  gold:     "#C08A2E",
  goldSoft: "#EFDDB0",
  tulsi:    "#0E7C55",
  ivory:    "#FFF8E6",
};

const TRADITION_TINT = {
  hindu:     { bg: "bg-amber-50",   ring: "ring-amber-200",   text: "text-amber-900" },
  muslim:    { bg: "bg-emerald-50", ring: "ring-emerald-200", text: "text-emerald-900" },
  sikh:      { bg: "bg-orange-50",  ring: "ring-orange-200",  text: "text-orange-900" },
  christian: { bg: "bg-sky-50",     ring: "ring-sky-200",     text: "text-sky-900" },
  secular:   { bg: "bg-slate-50",   ring: "ring-slate-200",   text: "text-slate-900" },
};

export default function IndianFamilyTreeFestivalDetail() {
  const { slug } = useParams();
  const [festival, setFestival] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFestival(null);
    setNotFound(false);
    (async () => {
      try {
        const r = await http.get(`/ift/festivals/${slug}`);
        if (!cancelled) setFestival(r.data);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen" style={{ background: P.bg, color: P.ink }}>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="ift-festival-not-found">
          <Flame className="mx-auto h-8 w-8" style={{ color: P.saffron }} />
          <h1 className="mt-3 font-serif text-2xl" style={{ color: P.ink }}>Festival not found</h1>
          <p className="mt-2 text-sm" style={{ color: P.inkSoft }}>
            This entry may have been renamed or archived.
          </p>
          <Link
            to="/indian-family-tree/festivals"
            className="mt-4 inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-white"
            style={{ background: P.saffron }}
          >
            <ArrowLeft className="h-3 w-3" /> All festivals
          </Link>
        </div>
      </div>
    );
  }

  if (!festival) {
    return (
      <div className="min-h-screen" style={{ background: P.bg, color: P.ink }}>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm" style={{ color: P.inkSoft }}>
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Opening the archive…
        </div>
      </div>
    );
  }

  const tint = TRADITION_TINT[festival.tradition] || TRADITION_TINT.secular;
  const crumb = [
    { name: "IndianFamilyTree", url: "https://indianfamilytree.com/indian-family-tree" },
    { name: "Festivals",        url: "https://indianfamilytree.com/indian-family-tree/festivals" },
    { name: festival.name,      url: `https://indianfamilytree.com/indian-family-tree/festivals/${festival.slug}` },
  ];

  return (
    <div className="min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid={`ift-festival-${festival.slug}`}>
      <SeoHead
        section="indianfamilytree"
        title={`${festival.name} — Indian Family Tree`}
        description={festival.summary?.slice(0, 220)}
      />
      <JsonLd data={breadcrumbSchema(crumb)} />

      <header className="border-b" style={{ background: P.bgDeep, borderColor: P.gold }}>
        <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
          <Link
            to="/indian-family-tree/festivals"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest"
            style={{ color: P.tulsi }}
            data-testid="ift-festival-back"
          >
            <ArrowLeft className="h-3 w-3" /> Festivals
          </Link>
          <div className="mt-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest" style={{ color: P.inkSoft }}>
            <Calendar className="h-3.5 w-3.5" /> {festival.month} · {festival.typical_date}
          </div>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl" style={{ color: P.saffron }}>
            {festival.name}
          </h1>
          {festival.also_known_as?.length > 0 && (
            <div className="mt-2 text-sm italic" style={{ color: P.inkSoft }}>
              Also known as: {festival.also_known_as.join(" · ")}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ring-1 ${tint.bg} ${tint.ring} ${tint.text}`}>
              {festival.tradition}
            </span>
            {festival.regions?.map((r) => (
              <span
                key={r}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px]"
                style={{ background: P.goldSoft, color: P.ink }}
              >
                <MapPin className="h-2.5 w-2.5" /> {r}
              </span>
            ))}
          </div>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
        {festival.summary && (
          <section data-testid="ift-festival-summary">
            <p className="whitespace-pre-line font-serif text-base leading-relaxed" style={{ color: P.ink }}>
              {festival.summary}
            </p>
          </section>
        )}

        {festival.family_customs && (
          <section
            className="mt-8 rounded-2xl border p-5"
            style={{ background: P.ivory, borderColor: P.gold }}
            data-testid="ift-festival-customs"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest" style={{ color: P.saffron }}>
              <ScrollText className="h-3.5 w-3.5" /> Family customs
            </div>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed" style={{ color: P.inkSoft }}>
              {festival.family_customs}
            </p>
          </section>
        )}

        <div className="mt-10 text-center text-[11px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>
          Your family's version of this festival looks different? →{" "}
          <Link to="/indian-family-tree" className="underline" style={{ color: P.tulsi }}>
            Contribute
          </Link>
        </div>
      </article>
    </div>
  );
}
